import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTimelineStream } from '@/hooks/streaming/use-timeline-stream';
import { useClient } from '@/hooks/use-client';
import { useScopeUrl } from '@/hooks/use-scope-url';
import { fetchAndImportStatuses } from '@/queries/utils/fetch-statuses';
import {
  useTimelinesStore,
  useTimeline as useStoreTimeline,
  useTimelinesActions,
  type TimelineEntry,
} from '@/stores/timelines';
import { compareId } from '@/utils/comparators';

import { queryKeys } from '../keys';
import { scopedQueryKey } from '../query';

import type { PaginatedResponse, PaginationParams, Status, StreamingParams } from 'pl-api';

type TimelineFetcher = (params?: PaginationParams) => Promise<PaginatedResponse<Status>>;

interface StreamConfig {
  stream: string;
  params?: StreamingParams;
}

interface TimelineOptions {
  polling?: boolean;
  restoringMaxId?: string;
  prefetchRebloggedRelationships?: boolean;
}

const POLLING_INTERVAL = 20_000;
const GAP_PAGE_SIZE = 10;

const sinceIdUnsupported = (statuses: Status[], sinceId: string) =>
  statuses.length > 0 && statuses.some((status) => compareId(status.id, sinceId) <= 0);

const useTimeline = (
  timelineId: string,
  fetcher: TimelineFetcher,
  streamConfig?: StreamConfig,
  options?: TimelineOptions,
) => {
  const polling = options?.polling ?? true;
  const restoringMaxId = options?.restoringMaxId;
  const pendingRestoringMaxId = useRef(restoringMaxId);

  const scopeUrl = useScopeUrl();
  const timeline = useStoreTimeline(scopeUrl, timelineId);
  const pollingEnabled =
    useTimelinesStore((state) => state.pollingEnabled) && !timeline.pollingUnsupported;
  const timelineActions = useTimelinesActions();
  const queryClient = useQueryClient();
  const client = useClient();

  const streamConfigRef = useRef(streamConfig);
  streamConfigRef.current = streamConfig;

  const { updatesAvailable: streamingAvailable } = useTimelineStream(
    streamConfig?.stream ?? '',
    streamConfig?.params,
    !!streamConfig?.stream,
  );

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const newestStatusId = useRef<string | undefined>(undefined);

  const fetchMissingRelationships = useCallback(
    async (items: Array<Status>) => {
      if (!options?.prefetchRebloggedRelationships) return;

      const missingRebloggedAccountIds = [
        ...new Set(items.filter((status) => !!status.reblog).map((status) => status.account.id)),
      ].filter(
        (accountId) =>
          queryClient.getQueryData(
            scopedQueryKey(queryKeys.accountRelationships.show(accountId), scopeUrl),
          ) === undefined,
      );

      if (missingRebloggedAccountIds.length > 0) {
        const relationships = await client.accounts.getRelationships(missingRebloggedAccountIds);

        for (const relationship of relationships) {
          queryClient.setQueryData(
            scopedQueryKey(queryKeys.accountRelationships.show(relationship.id), scopeUrl),
            relationship,
          );
        }
      }
    },
    [client, queryClient, scopeUrl, options?.prefetchRebloggedRelationships],
  );

  useEffect(() => {
    if (timeline.entries[0]?.type === 'status') {
      newestStatusId.current = timeline.entries[0].originalId;
    }
  }, [timeline.entries]);

  // Keep REST fallback when streaming delivery is uncertain, even on an open socket.
  useEffect(() => {
    if (
      !polling ||
      !pollingEnabled ||
      streamingAvailable ||
      timeline.isPending ||
      !(timeline.newestStatusId ?? newestStatusId.current)
    )
      return;

    let active = true;
    let inFlight = false;
    const poll = async () => {
      if (!active || inFlight) return;
      const sinceId =
        useTimelinesStore.getState().timelines[scopeUrl]?.[timelineId]?.newestStatusId ??
        newestStatusId.current;
      if (!sinceId) return;

      inFlight = true;
      try {
        const response = await fetchAndImportStatuses(
          scopeUrl,
          () => fetcherRef.current({ since_id: sinceId }),
          (response) =>
            active && !sinceIdUnsupported(response.items, sinceId) ? response.items : [],
        );
        if (!active) return;
        if (sinceIdUnsupported(response.items, sinceId)) {
          timelineActions.disablePolling(scopeUrl, timelineId);
          return;
        }

        if (response.items.length === 0) return;

        await fetchMissingRelationships(response.items);
        if (!active) return;

        for (const status of response.items) {
          timelineActions.receiveStreamingStatus(scopeUrl, timelineId, status);
        }
      } catch {
      } finally {
        inFlight = false;
      }
    };

    const interval = setInterval(poll, POLLING_INTERVAL);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [
    scopeUrl,
    timelineId,
    polling,
    pollingEnabled,
    streamingAvailable,
    timeline.isPending,
    timeline.newestStatusId,
    timelineActions,
    fetchMissingRelationships,
  ]);

  useEffect(() => {
    if (!timeline.isPending || timeline.isFetching || timeline.isError === 401) return;
    fetchInitial();
  }, [scopeUrl, timelineId, timeline.isPending]);

  const fetchInitial = useCallback(
    async (isRestoring = !!pendingRestoringMaxId.current) => {
      const restoringFromId = isRestoring ? pendingRestoringMaxId.current : undefined;
      pendingRestoringMaxId.current = undefined;
      timelineActions.setLoading(scopeUrl, timelineId, true);
      try {
        const [response, shouldInsertGap] = await fetchAndImportStatuses(
          scopeUrl,
          () =>
            Promise.all([
              fetcherRef.current(),
              !restoringFromId
                ? Promise.resolve(false)
                : fetcherRef
                    .current({ since_id: restoringFromId, limit: 1 })
                    .then((res) => res.items.length > 0)
                    .catch(() => true),
            ]),
          ([response]) => response.items,
        );
        await fetchMissingRelationships(response.items);

        timelineActions.expandTimeline(
          scopeUrl,
          timelineId,
          response.items,
          !!response.next,
          true,
          isRestoring && shouldInsertGap,
        );
      } catch (error) {
        timelineActions.setError(
          scopeUrl,
          timelineId,
          true,
          (error as { response?: { status?: number } }).response?.status,
        );
      }
    },
    [scopeUrl, timelineId],
  );

  const fetchNextPage = useCallback(async () => {
    if (timeline.isFetching) return;

    timelineActions.setLoading(scopeUrl, timelineId, true);

    try {
      const response = await fetchAndImportStatuses(
        scopeUrl,
        () => fetcherRef.current({ max_id: timeline.oldestStatusId }),
        (response) => response.items,
      );
      await fetchMissingRelationships(response.items);

      timelineActions.expandTimeline(scopeUrl, timelineId, response.items, !!response.next, false);
    } catch (error) {
      timelineActions.setError(
        scopeUrl,
        timelineId,
        true,
        (error as { response?: { status?: number } }).response?.status,
      );
    }
  }, [scopeUrl, timelineId, timeline.oldestStatusId, timeline.isFetching]);

  const dequeueEntries = useCallback(() => {
    timelineActions.dequeueEntries(scopeUrl, timelineId);
  }, [scopeUrl, timelineId]);

  const fillGap = useCallback(
    async (gap: Extract<TimelineEntry, { type: 'gap' }>, direction: 'up' | 'down') => {
      let params: PaginationParams;
      if (direction === 'up') {
        const gapIndex = timeline.entries.indexOf(gap);
        const previousEntry = gapIndex > 0 ? timeline.entries[gapIndex - 1] : null;
        const maxId =
          previousEntry && previousEntry.type === 'status' ? previousEntry.originalId : undefined;
        params = { min_id: gap.minId, max_id: maxId, limit: GAP_PAGE_SIZE };
      } else {
        params = { max_id: gap.maxId, since_id: gap.minId, limit: GAP_PAGE_SIZE };
      }

      const { statuses, loadMore } = await fetchAndImportStatuses(
        scopeUrl,
        async () => {
          let response = await fetcherRef.current(params);
          const statuses = [...response.items];
          const entryIds = new Set(statuses.map((status) => (status.reblog || status).id));
          let loadMore = direction === 'up' ? response.previous : response.next;

          // A server may return a sparse page with a continuation link. Follow those
          // links until this action can show a full page or the bounded gap is exhausted.
          while (entryIds.size < GAP_PAGE_SIZE && loadMore) {
            response = await loadMore();
            if (direction === 'up') statuses.unshift(...response.items);
            else statuses.push(...response.items);
            response.items.forEach((status) => entryIds.add((status.reblog || status).id));
            loadMore = direction === 'up' ? response.previous : response.next;
          }

          return { statuses, loadMore };
        },
        (response) => response.statuses,
      );
      await fetchMissingRelationships(statuses);

      timelineActions.fillGap(scopeUrl, timelineId, gap.minId, statuses, !!loadMore, direction);
    },
    [scopeUrl, timelineId],
  );

  const refetch = useCallback(() => {
    timelineActions.resetTimeline(scopeUrl, timelineId);
    return fetchInitial(false);
  }, [scopeUrl, timelineId, fetchInitial]);

  return useMemo(
    () => ({
      ...timeline,
      scopeUrl,
      timelineId,
      fetchNextPage,
      dequeueEntries,
      fillGap,
      refetch,
      hasStreamConfig: !!streamConfig,
      options,
    }),
    [
      timeline,
      scopeUrl,
      timelineId,
      fetchNextPage,
      dequeueEntries,
      fillGap,
      refetch,
      !!streamConfig,
    ],
  );
};

export { useTimeline };
